import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { db } from "../firebase/firebaseConfig";
import {
  collection,
  getDocs,
  query,
  limit,
  startAfter,
  addDoc,
  where,
} from "firebase/firestore";
import { User } from "lucide-react";
import { motion } from "framer-motion";
import { UserContext } from "../context/UserContext";
import { useNavigate } from "react-router-dom";
import AuthModal from "../components/modal/AuthModal";
import useSEO from "../hooks/useSeo";

const PAGE_SIZE = 10;

const DevelopersList = () => {
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useContext(UserContext) || {};
  const navigate = useNavigate();
  useSEO({
    title: "Find Developers | Hire Top Talent on SolveMyCodeIssue.com",
    description:
      "Discover skilled developers ready to solve your coding issues, build projects, or collaborate. Browse verified profiles, view expertise, and hire instantly on SolveMyCodeIssue.com.",
    keywords:
      "find developers, hire developers online, freelance programmers, coding experts, software developers, developer profiles, Solve My Code Issue",
    canonical: "https://solvemycodeissue.com/developers",
    ogImage: "https://solvemycodeissue.com/og-image-developers.png",
  });
  const fetchPage = async (direction = "first") => {
    setLoading(true);
    try {
      if (direction === "prev" && currentPageIndex > 0) {
        const prevPage = pages[currentPageIndex - 1];
        setDevelopers(prevPage.data);
        setCurrentPageIndex((prev) => prev - 1);
        setLoading(false);
        return;
      }

      let q;
      if (direction === "next") {
        const lastDoc = pages[currentPageIndex]?.lastDoc;
        if (!lastDoc) return;
        q = query(
          collection(db, "users"),
          startAfter(lastDoc),
          limit(PAGE_SIZE)
        );
      } else {
        q = query(collection(db, "users"), limit(PAGE_SIZE));
      }

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const devs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const lastDoc = snapshot.docs[snapshot.docs.length - 1];
        const isLast = snapshot.docs.length < PAGE_SIZE;
        const newPage = { data: devs, lastDoc, isLast };

        if (direction === "first") {
          setPages([newPage]);
          setCurrentPageIndex(0);
        } else if (direction === "next") {
          setPages((prev) => [...prev, newPage]);
          setCurrentPageIndex((prev) => prev + 1);
        }

        setDevelopers(devs);
      }
    } catch (err) {
      console.error("Error fetching developers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage("first");
  }, []);

  const handleChat = async (dev) => {
    try {
      if (!user || !user.id) return setShowAuthModal(true);

      const chatsRef = collection(db, "chats");
      const q = query(chatsRef, where("members", "array-contains", user.id));
      const snapshot = await getDocs(q);

      let chatId = null;
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.members.includes(dev.id)) chatId = docSnap.id;
      });

      if (!chatId) {
        const newChat = await addDoc(collection(db, "chats"), {
          members: [user.id, dev.id],
          membersInfo: {
            [user.id]: {
              name: user.name,
              email: user.email,
              profilePic: user.profilePic || null,
            },
            [dev.id]: {
              name: dev.name || "Unnamed User",
              email: dev.email || null,
              profilePic: dev.profilePic || null,
            },
          },
          createdAt: new Date(),
          messages: [],
        });
        chatId = newChat.id;
      }
      navigate("/my-chats");
    } catch (err) {
      console.error("Error initiating chat:", err);
    }
  };

  return (
    <PageWrapper>
      <Title>👩‍💻 SolveMyCodeIssue Members</Title>

      {loading ? (
        <Loading>Loading developers...</Loading>
      ) : developers.length === 0 ? (
        <Empty>No members found!</Empty>
      ) : (
        <>
          <DevelopersGrid>
            {developers.map((dev, i) => (
              <motion.div
                key={dev.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <DevCard>
                  {dev.profilePic ? (
                    <DevAvatar src={dev.profilePic} alt={dev.name} />
                  ) : (
                    <DevAvatarFallback>
                      {dev.name ? dev.name.charAt(0).toUpperCase() : <User />}
                    </DevAvatarFallback>
                  )}

                  <DevInfo>
                    <h3>{dev.name || "Unnamed Developer"}</h3>
                    <p>{dev.email || "No email"}</p>
                    <Skills>
                      {dev.skills?.length
                        ? dev.skills
                            .slice(0, 3)
                            .map((s, idx) => <SkillTag key={idx}>{s}</SkillTag>)
                        : "No skills listed"}
                    </Skills>
                  </DevInfo>

                  <Actions>
                    <ActionButton
                      onClick={() => {
                        if (user && user.id === dev.id)
                          navigate(`/profile/${user.id}`);
                        else navigate(`/developers/${dev.id}`);
                      }}
                    >
                      View Profile
                    </ActionButton>

                    {user && user.id !== dev.id ? (
                      <ActionButton secondary onClick={() => handleChat(dev)}>
                        Chat
                      </ActionButton>
                    ) : (
                      !user && (
                        <ActionButton
                          secondary
                          onClick={() => setShowAuthModal(true)}
                        >
                          Chat
                        </ActionButton>
                      )
                    )}
                  </Actions>
                </DevCard>
              </motion.div>
            ))}
          </DevelopersGrid>

          <Pagination>
            <PageButton
              onClick={() => fetchPage("prev")}
              disabled={currentPageIndex === 0}
            >
              ⬅ Prev
            </PageButton>
            <PageButton
              onClick={() => fetchPage("next")}
              disabled={pages[currentPageIndex]?.isLast}
            >
              Next ➡
            </PageButton>
          </Pagination>
        </>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </PageWrapper>
  );
};

export default DevelopersList;

/* ---------------- Styled Components ---------------- */

const PageWrapper = styled.div`
  background: radial-gradient(circle at top left, #0b0617, #000);
  min-height: 100vh;
  color: #fff;
  padding: 60px 20px;
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2rem;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 40px;
`;

const Loading = styled.p`
  text-align: center;
  color: #aaa;
`;

const Empty = styled.p`
  text-align: center;
  color: #aaa;
`;

const DevelopersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 30px;
  justify-content: center;
`;

const DevCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  transition: 0.3s ease;
  backdrop-filter: blur(6px);
  box-shadow: 0 0 20px rgba(155, 92, 255, 0.1);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 25px rgba(155, 92, 255, 0.3);
    border-color: #c59fff;
  }
`;

const DevAvatar = styled.img`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  border: 2px solid #c59fff;
  object-fit: cover;
  margin-bottom: 15px;
`;

const DevAvatarFallback = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9b5cff, #c59fff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
  font-size: 28px;
  margin: 0 auto 15px;
`;

const DevInfo = styled.div`
  h3 {
    color: #fff;
    font-size: 1.1rem;
    margin-bottom: 6px;
  }
  p {
    color: #bbb;
    font-size: 0.9rem;
    margin-bottom: 6px;
  }
`;

const Skills = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
`;

const SkillTag = styled.span`
  background: rgba(155, 92, 255, 0.15);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 0.8rem;
  color: #c59fff;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
`;

const ActionButton = styled.button`
  padding: 8px 14px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  background: ${(props) =>
    props.secondary
      ? "rgba(255,255,255,0.08)"
      : "linear-gradient(90deg, #9b5cff, #c59fff)"};
  color: #fff;
  transition: 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 12px rgba(155, 92, 255, 0.4);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
`;

const PageButton = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(155, 92, 255, 0.3);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    background: rgba(155, 92, 255, 0.2);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
